import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import Modal from "./Modal";
import supabase from "../../utils/supabase";
import useUserStore from "../../stores/user";
import {
  getCourseTypesForState,
  type CourseTypeOption,
} from "../../config/courseTypes";

interface State {
  id: number;
  name: string;
}

interface AddDistrictModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess?: () => void;
}

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required("District name is required")
    .min(2, "District name must be at least 2 characters"),
  state: yup.number().nullable(),
  courseType: yup.string().nullable(),
});

export default function AddDistrictModal({
  isOpen,
  onClose,
  onSuccess,
}: AddDistrictModalProps) {
  const user = useUserStore((state) => state.user);
  const [states, setStates] = useState<State[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [defaultStateId, setDefaultStateId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchStates();
    }
  }, [isOpen]);

  async function fetchStates() {
    setLoadingStates(true);
    try {
      const { data, error } = await supabase
        .from("state")
        .select("id, name")
        .order("name", { ascending: true });

      if (error) {
        console.error("Error fetching states:", error);
        toast.error("Failed to fetch states");
        return;
      }

      setStates(data || []);

      // Find Oklahoma and set as default
      const oklahoma = data?.find(
        (state) => state.name.toLowerCase() === "oklahoma"
      );
      if (oklahoma) {
        setDefaultStateId(oklahoma.id);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Failed to fetch states");
    } finally {
      setLoadingStates(false);
    }
  }

  const handleSubmit = async (
    values: {
      name: string;
      state: string | number | null;
      courseType: string;
    },
    { setSubmitting, resetForm }: any
  ) => {
    if (!user) {
      toast.error("User not authenticated");
      setSubmitting(false);
      return;
    }

    try {
      // Convert state to number if provided
      let stateId: number | null = null;
      if (values.state !== undefined && values.state !== "") {
        stateId =
          typeof values.state === "string"
            ? Number(values.state)
            : values.state;
      }

      const { error } = await supabase.from("districts").insert([
        {
          name: values.name,
          user_id: user.id,
          state: stateId,
          course_type: values.courseType || null,
        },
      ]);

      if (error) {
        console.error("Error adding district:", error);
        toast.error("Failed to add district");
        return;
      }

      toast.success("District added successfully!");
      resetForm();
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Error adding district:", error);
      toast.error("Failed to add district");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New District">
      <Formik
        initialValues={{
          name: "",
          state:
            typeof defaultStateId === "number" ? String(defaultStateId) : "",
          courseType: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => {
          // Check if selected state has multiple course types
          const selectedStateId = formik.values.state;
          const selectedState = states.find(
            (s) => String(s.id) === String(selectedStateId)
          );
          const courseTypes: CourseTypeOption[] | null = selectedState
            ? getCourseTypesForState(selectedState.name)
            : null;

          return (
            <Form className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  District Name
                </label>
                <Field
                  id="name"
                  name="name"
                  className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Enter district name"
                />
                {formik.errors.name && formik.touched.name && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.name}
                  </div>
                )}
              </div>

              <div>
                <label
                  htmlFor="state"
                  className="block text-sm font-medium text-gray-700"
                >
                  State
                </label>
                <Field
                  as="select"
                  name="state"
                  id="state"
                  className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
                  disabled={loadingStates}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    formik.handleChange(e);
                    // Reset course type when state changes
                    formik.setFieldValue("courseType", "");
                  }}
                >
                  <option value="">Select a state</option>
                  {states.map((state) => (
                    <option key={state.id} value={String(state.id)}>
                      {state.name}
                    </option>
                  ))}
                </Field>
                {formik.errors.state && formik.touched.state && (
                  <div className="text-red-500 text-sm mt-1">
                    {typeof formik.errors.state === "string"
                      ? formik.errors.state
                      : "Invalid state selection"}
                  </div>
                )}
              </div>

              {courseTypes && (
                <div>
                  <label
                    htmlFor="courseType"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Course Type
                  </label>
                  <Field
                    as="select"
                    name="courseType"
                    id="courseType"
                    className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="">Select a course</option>
                    {courseTypes.map((ct) => (
                      <option key={ct.value} value={ct.value}>
                        {ct.label}
                      </option>
                    ))}
                  </Field>
                </div>
              )}

              <div className="pt-4 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-small btn-secondary"
                  disabled={formik.isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn btn-small ${
                    formik.isSubmitting ? "btn-disabled" : ""
                  }`}
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  {formik.isSubmitting ? "Adding..." : "Add District"}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}
